import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Radio } from '@mui/material';
import { UUID } from '@myme/utils';
import { ReportIcon } from 'src/icons';
import { usePalette, useTranslation } from 'src/hooks';
import { toast, ToastType } from 'src/utils';
import { SvgIcon, Typography } from 'src/components/atoms';
import { Flex, FlexColumn } from 'src/components/layout';
import { Dialog } from 'src/components/organisms';
import { useGetReportReasons } from 'src/components/pages/mymes/details/useGetReportReasons';
import { useReportMymeMutation } from './useReportMymeMutation';

interface Props {
  mymeId: UUID;
  onClose: () => void;
}

const StyledReportIconContainer = styled(SvgIcon)`
  margin-bottom: 1rem;
  height: 4rem;
  width: 4rem;
`;

export const ReportMymeDialog = ({ mymeId, onClose }: Props) => {
  const { t } = useTranslation();
  const { reportReasons, isLoading } = useGetReportReasons();
  const [chosenReasonId, setChosenReasonId] = useState<UUID | null>(null);

  const handleClearFormOnSuccess = () => {
    onClose();
  };
  const reportMymeMutation = useReportMymeMutation(handleClearFormOnSuccess);

  const handleChangeReason = (e: React.ChangeEvent<HTMLInputElement>) => setChosenReasonId(e.target.value);

  const handleReportMyme = () => {
    if (!chosenReasonId) {
      toast(ToastType.ERROR, t('errors.validation.mymes.details.review_report_option_not_chosen'));
      return;
    }

    reportMymeMutation.mutate({ mymeId, reportId: chosenReasonId });
  };

  if (isLoading) {
    return null;
  }

  return (
    <Dialog
      title={t('pages.mymes.details.report_myme_dialog.title')}
      onClose={onClose}
      defaultActionButtons={{
        action: handleReportMyme,
        btnConfirmText: t('pages.mymes.details.report_myme_dialog.btn'),
        otherActionBtnProps: {
          type: 'submit',
          disabled: reportMymeMutation.isLoading
        }
      }}
      enableBackdropClick
      enableEscapeKeyDown
    >
      <FlexColumn alignItems='center'>
        <StyledReportIconContainer>
          <ReportIcon />
        </StyledReportIconContainer>
        <FlexColumn>
          {reportReasons.map(reason => (
            <SingleReportOption
              value={reason.id}
              isChecked={chosenReasonId === reason.id}
              text={reason.name}
              onChange={handleChangeReason}
              key={reason.id}
            />
          ))}
        </FlexColumn>
      </FlexColumn>
    </Dialog>
  );
};

interface SingleReportOptionProps {
  value: UUID;
  isChecked: boolean;
  text: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const SingleReportOption = ({ value, isChecked, text, onChange }: SingleReportOptionProps) => {
  const { text: textColor } = usePalette();
  return (
    <Flex>
      <Radio
        sx={{ color: textColor.primary }}
        size='small'
        value={value}
        checked={isChecked}
        onChange={onChange}
        data-testid={`report reason ${value}`}
      />
      <Typography variant='body2'>{text}</Typography>
    </Flex>
  );
};
